import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import PageHeader from '../../components/ui/PageHeader';
import Card, { CardHeader } from '../../components/ui/Card';
import StatCard from '../../components/ui/StatCard';
import ProgressBar from '../../components/ui/ProgressBar';
import TierBreakdownBar from '../../components/ui/TierBreakdownBar';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import Skeleton, { SkeletonStatCard } from '../../components/ui/Skeleton';
import { Icon } from '../../components/ui/Icon';
import EvaluateDialog from '../Periods/EvaluateDialog';
import { fetchDashboard } from '../../store/slices/dashboardSlice';
import { fetchPeriods } from '../../store/slices/periodsSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { formatDate, formatASPara } from '../../utils/format';
import apiClient from '../../services/apiClient';

const PROGRESS_COLORS = {
  success: 'success',
  primary: 'primary',
  warning: 'warning',
  purple: 'primary',
  pink: 'danger',
};

// Durum → renk + etiket
const TRANSFER_STATUS = {
  fresh:   { color: 'green',  label: 'Güncel'      },
  stale:   { color: 'yellow', label: 'Gecikmiş'    },
  missing: { color: 'red',    label: 'Eksik'       },
  error:   { color: 'gray',   label: 'Bilinmiyor'  },
};

// Aktarım takvimi → etiket + renk
const SCHEDULE_META = {
  daily:   { label: 'Günlük', color: 'blue'   },
  monthly: { label: 'Aylık',  color: 'purple' },
};

/** Satır sayısını insan okunur formata çevirir: 12400 → "12.4K" */
function formatRowCount(n) {
  if (n < 0) return null;
  if (n === 0) return '0 satır';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M satır`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K satır`;
  return `${n} satır`;
}

/** ISO tarih string'ini "27 Nis 14:35" formatında döner (İstanbul UTC+3) */
function formatTransferDate(iso) {
  if (!iso) return null;
  try {
    // Backend UTC olarak saklıyor — Z suffix yoksa UTC olarak zorla
    const normalized = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
    return new Date(normalized).toLocaleString('tr-TR', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Istanbul',
    });
  } catch {
    return null;
  }
}

// Backend yanıt şekli (snake_case) -> Dashboard JSX'inin beklediği kamel/iç yapıya
// dönüştürür. Bu mapper'ı slice yerine burada tutuyoruz çünkü activePeriod için
// periodsSlice'tan gerçek periyot satırını çapraz referans etmemiz gerekiyor
// (backend dashboard endpoint'i aktif periyodun full objesini döndürmüyor, sadece
// period_progress içinde ad + id veriyor).
function useDashboardViewModel(raw, periods, selectedPeriodId) {
  return useMemo(() => {
    if (!raw) return null;

    // Tüm aktif dönemler — selector dropdown ve fallback için.
    const activePeriods = (periods || []).filter((p) => p.status === 'ACTIVE');

    // Hangi aktif dönem gösterilecek? Öncelik:
    //   1) selectedPeriodId verilmiş ve aktif dönemler arasında varsa onu seç
    //      (admin dropdown'dan seçmiş, localStorage'da hatırlanan değer).
    //   2) En son oluşturulan aktif dönem (created_at desc → ilk eleman).
    //   3) Hiç aktif dönem yoksa backend'in period_progress[0] fallback'i.
    let activePeriod = null;
    if (selectedPeriodId != null) {
      activePeriod = activePeriods.find((p) => String(p.id) === String(selectedPeriodId)) || null;
    }
    if (!activePeriod) activePeriod = activePeriods[0] || null;
    if (!activePeriod && raw.period_progress?.[0]) {
      activePeriod = {
        id: raw.period_progress[0].period_id,
        name: raw.period_progress[0].name,
        status: 'ACTIVE',
        start_date: null,
        end_date: null,
        reward_template: { tiers: [], baraj_puan: 0 },
        max_total_points: 0,
      };
    }

    // days_left — end_date varsa bugünden kalan gün sayısı.
    //
    // NOT: Saat dilimi tuzağı — `new Date('2026-05-31')` UTC midnight
    // (local 03:00 +03 TR), `new Date(); setHours(0,0,0,0)` ise LOCAL
    // midnight verir. Çıkarmada saat farkı kalıyor (~0.125 gün) ve
    // `Math.ceil` 23'ü 24'e yuvarlıyor (off-by-one bug).
    //
    // Düzeltme: iki tarafı da UTC midnight olarak normalize edip
    // tam gün farkı al — saat dilimi karışmıyor, integer çıkıyor.
    let days_left = null;
    if (activePeriod?.end_date) {
      // 'YYYY-MM-DD' veya tam ISO — yyyy/mm/dd parçalarını al.
      const m = String(activePeriod.end_date).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const endUTC = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        const now = new Date();
        const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
        days_left = Math.max(0, Math.round((endUTC - todayUTC) / 86400000));
      }
    }

    const lastJob = raw.recent_evaluations?.[0] || null;
    const lastSuccess = lastJob?.status === 'SUCCESS';
    const lastTime = raw.last_evaluated_at
      ? new Date(raw.last_evaluated_at).toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

    // baraj + total max — active period reward_template'inden.
    const barajPuan = Number(activePeriod?.reward_template?.baraj_puan ?? 0);
    const totalMaxPoint = Number(activePeriod?.max_total_points ?? 0);

    // Rewards — backend rank gold/silver/bronze döndürüyor, frontend sayısal
    // rank bekliyor. Sırayla 1,2,3 atayalım.
    const rewards = (raw.rewards_by_rank || []).map((r, i) => ({
      rank: i + 1,
      amount: Number(r.aspara || 0),
    }));

    // Data transfers — backend {source, label, schedule, last_write, row_count, status}
    const dataTransfers = (raw.data_transfers || []).map((t) => ({
      key:      t.source,
      name:     t.label || t.source,
      schedule: t.schedule || 'daily',       // "daily" | "monthly"
      date:     t.last_write || null,        // system.parts modification_time
      rowCount: typeof t.row_count === 'number' ? t.row_count : -1,
      status:   t.status || 'missing',
    }));

    // Evaluate logs — recent_evaluations'tan türet.
    const evaluateLogs = (raw.recent_evaluations || []).map((e) => {
      const timeStr = e.finished_at
        ? new Date(e.finished_at).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '—';
      const dryTag = e.is_dry_run ? ' (dry-run)' : '';
      const durTag = e.duration_s ? ` · ${e.duration_s.toFixed(2)}s` : '';
      const statusKind =
        e.status === 'SUCCESS'
          ? 'success'
          : e.status === 'PARTIAL_FAILED'
            ? 'warning'
            : ['FAILED', 'CRASHED', 'ROLLED_BACK'].includes(e.status)
              ? 'error'
              : 'pending';
      return {
        time: timeStr,
        // Otomatik (cron) vs manuel tetikleme — backend is_automatic döndürür.
        type: e.is_automatic ? 'cron' : 'manual',
        message: `${e.period_name} — ${e.status}${dryTag}${durTag}`,
        status: statusKind,
      };
    });

    return {
      activePeriod: activePeriod ? { ...activePeriod, days_left } : null,
      // Tüm aktif dönemler — Dashboard üstündeki dönem seçici dropdown'da
      // listelenmek için. Tek dönemde dropdown gizli; 2+ dönemde admin
      // hangisini görmek istediğini seçer.
      activePeriods,
      stats: {
        active_tasks: raw.active_tasks ?? 0,
        total_players: raw.total_players ?? 0,
        total_sks: raw.total_players ?? 0,
        total_managers: 0,
        last_evaluate_time: lastTime,
        last_evaluate_success: lastSuccess,
      },
      barajPuan,
      totalMaxPoint,
      periodProgress: raw.period_progress_tasks ?? [],
      rewards,
      dataTransfers,
      evaluateLogs,
    };
  }, [raw, periods, selectedPeriodId]);
}

/** Backend UTC saatini tarayıcının yerel saatine çevirir: 6 → "09:00" (UTC+3) */
function utcHourToLocal(utcHour) {
  const d = new Date();
  d.setUTCHours(utcHour, 0, 0, 0);
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
const CRON_UTC_HOUR = Number(import.meta.env.VITE_EVALUATE_CRON_HOUR ?? 6);
const CRON_LOCAL_LABEL = `Cron ${utcHourToLocal(CRON_UTC_HOUR)}`;

// localStorage key — admin'in son seçtiği dönemi hatırla. Sayfa yenilenince
// veya başka sekmeden dönünce aynı dönem otomatik seçili gelir.
const LS_KEY_DASHBOARD_PERIOD = 're_dashboard_selected_period_id';

// Worker job'ı kuyruktan çıkıp tamamlandığında dönen status değerleri.
// Bu set'tekilerden biri görünene kadar polling devam ediyor.
const TERMINAL_JOB_STATUSES = new Set(['SUCCESS', 'FAILED', 'PARTIAL_FAILED', 'ROLLED_BACK']);
// Polling pencere ayarları — 60 saniye boyunca dene, ilk poll 1.5s sonra
// (backend kuyruktan çekme + worker boot süresi için kısa buffer), sonrası
// 3s aralıklarla. Bu süre içinde job hâlâ terminal'e ulaşmadıysa "manuel
// yenileyin" toast'ı atılıp polling durduruluyor.
const POLL_FIRST_DELAY_MS = 1500;
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 60000;

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: rawData, status, error } = useSelector((s) => s.dashboard);
  const periodsList = useSelector((s) => s.periods.items);
  const [evaluateOpen, setEvaluateOpen] = useState(false);

  // Dönem seçici — birden fazla aktif dönem varsa admin seçer.
  // Init: localStorage'dan oku; null = "auto-select first ACTIVE".
  const [selectedPeriodId, setSelectedPeriodId] = useState(() => {
    try {
      const v = window.localStorage.getItem(LS_KEY_DASHBOARD_PERIOD);
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  });

  // Dry-run state
  const [dryRunTab, setDryRunTab]   = useState('SKS');
  const [dryRunMeta, setDryRunMeta] = useState(null);
  const [dryRunRows, setDryRunRows] = useState([]);
  const [dryRunLoading, setDryRunLoading] = useState(false);

  // Pending job — admin "Şimdi Hesapla" tıkladığında EvaluateDialog
  // onJobQueued callback'i ile burası set edilir. Ardından polling başlar:
  // 3s aralıklarla dashboard yenilenir, recent_evaluations[job_id] terminal
  // status (SUCCESS/FAILED/...) görünene kadar veya 60s timeout'a kadar.
  // { jobId, periodId, dryRun, startedAt }
  const [pendingJob, setPendingJob] = useState(null);

  const data = useDashboardViewModel(rawData, periodsList, selectedPeriodId);

  useEffect(() => {
    dispatch(fetchPeriods());
  }, [dispatch]);

  // Dashboard verisi seçili periyoda bağlı — selectedPeriodId değiştikçe
  // yeniden çek. Backend `period_id=null` durumunda en güncel ACTIVE'i
  // otomatik seçiyor; bizim selectedPeriodId null ise null gönderiyoruz.
  useEffect(() => {
    dispatch(fetchDashboard(selectedPeriodId || undefined));
  }, [dispatch, selectedPeriodId]);

  // Persist seçimi — kullanıcı her değiştirdiğinde localStorage'a yaz.
  useEffect(() => {
    try {
      if (selectedPeriodId != null) {
        window.localStorage.setItem(LS_KEY_DASHBOARD_PERIOD, String(selectedPeriodId));
      }
    } catch { /* ignore */ }
  }, [selectedPeriodId]);

  // Periods listesi değişince selected'i validate et — admin başka tab'da
  // dönemi kapatmış olabilir. ACTIVE değilse seçimi temizle.
  useEffect(() => {
    if (selectedPeriodId == null) return;
    const stillActive = (periodsList || []).some(
      (p) => String(p.id) === String(selectedPeriodId) && p.status === 'ACTIVE',
    );
    if (!stillActive) setSelectedPeriodId(null);
  }, [periodsList, selectedPeriodId]);

  // Polling — admin yeni bir job kuyruğa attığında pendingJob set ediliyor;
  // burada 3s aralıklarla dashboard yenileniyor. Job terminal status'a
  // ulaşınca toast atılıyor ve polling duruyor. Timeout (60s) içinde hâlâ
  // dönmediyse kullanıcıya manuel refresh önerisi yapılıyor.
  useEffect(() => {
    if (!pendingJob) return undefined;
    let cancelled = false;
    let timer = null;
    const startedAt = pendingJob.startedAt || Date.now();

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await dispatch(fetchDashboard(selectedPeriodId || undefined));
        if (cancelled) return;
        const logs = res.payload?.recent_evaluations || [];
        const found = logs.find((l) => l.job_id === pendingJob.jobId);
        if (found && TERMINAL_JOB_STATUSES.has(String(found.status).toUpperCase())) {
          // Terminal — sonucu kullanıcıya bildir, polling'i durdur.
          const isSuccess = String(found.status).toUpperCase() === 'SUCCESS';
          dispatch(pushToast({
            kind: isSuccess ? 'success' : 'error',
            message: pendingJob.dryRun
              ? (isSuccess
                  ? `Dry-run tamamlandı — sıralama önizlemesi hazır.`
                  : `Dry-run başarısız: ${found.status}. Loglara bakın.`)
              : (isSuccess
                  ? `Hesaplama tamamlandı — sonuçlar yazıldı.`
                  : `Hesaplama başarısız: ${found.status}. Loglara bakın.`),
          }));
          // Hesaplama başarıyla bittiyse "Son Dry-Run Simülasyonu" kartını
          // otomatik tazele (Redux dışı state — fetchDashboard yetmiyor).
          if (isSuccess && pendingJob.dryRun && activePeriodId) {
            loadDryRun(activePeriodId, dryRunTab);
          }
          setPendingJob(null);
          return;
        }
      } catch {
        // Tek bir poll başarısız olduysa polling devam etsin; sonraki
        // tick'te tekrar dene. Network bir anlık olabilir.
      }
      if (cancelled) return;
      if (Date.now() - startedAt >= POLL_MAX_DURATION_MS) {
        dispatch(pushToast({
          kind: 'warning',
          message: 'Hesaplama hâlâ sürüyor. Manuel olarak Yenile butonuna basabilirsiniz.',
        }));
        setPendingJob(null);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    timer = setTimeout(tick, POLL_FIRST_DELAY_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pendingJob, dispatch, selectedPeriodId]);

  // Aktif dönem değişince veya tab değişince dry-run verisini çek
  const loadDryRun = useCallback(async (periodId, tab) => {
    if (!periodId) return;
    setDryRunLoading(true);
    try {
      const res = await apiClient.get('/rule-engine/leaderboard/dry-run', {
        params: { period_id: periodId, tab },
      });
      // apiClient interceptor envelope'u açar: res.data = { meta, data }
      const d = res.data ?? {};
      setDryRunMeta(d.meta ?? null);
      setDryRunRows(d.data ?? []);
    } catch {
      setDryRunMeta(null);
      setDryRunRows([]);
    } finally {
      setDryRunLoading(false);
    }
  }, []);

  const activePeriodId = data?.activePeriod?.id;
  useEffect(() => {
    if (activePeriodId) loadDryRun(activePeriodId, dryRunTab);
  }, [activePeriodId, dryRunTab, loadDryRun]);

  // Failed — API down / ERR_NAME_NOT_RESOLVED vb. Kullanıcıya açık hata +
  // retry göster; yoksa boş Dashboard ile takılı kalırdı.
  if (status === 'failed' && !data) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Dashboard" />
        <Card>
          <EmptyState
            icon="warning"
            title="Dashboard verileri alınamadı"
            message={
              error
                ? `Sunucuya ulaşılamıyor (${String(error)}). Servis yayına alındıktan sonra yeniden deneyin.`
                : 'Sunucuya ulaşılamıyor. Servis yayına alındıktan sonra yeniden deneyin.'
            }
            action={
              <Button
                variant="secondary"
                icon="refresh"
                onClick={() => {
                  dispatch(fetchPeriods());
                  dispatch(fetchDashboard(selectedPeriodId || undefined));
                }}
              >
                Tekrar Dene
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  // İlk yükleme — data henüz yok, skeleton layout göster.
  // Böylece sayfa ana iskeletiyle anında oturuyor, veri geldikçe kartlar
  // gerçek içerikle doluyor ("lazy load" hissi).
  if (!data) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-40" />
            <Skeleton className="mt-2 h-3 w-64" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        <Skeleton className="h-7 w-80 rounded-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonStatCard key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="mt-2 h-3 w-40" />
            <div className="mt-6 space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i}>
                  <div className="flex justify-between mb-2">
                    <Skeleton className="h-3 w-40" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-2 w-full" />
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <Skeleton className="h-5 w-36" />
            <Skeleton className="mt-2 h-3 w-48" />
            <div className="mt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50/50 px-3 py-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="mt-2 h-3 w-64" />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="rounded-lg border border-neutral-200 bg-white p-3 text-center">
                  <Skeleton className="mx-auto h-3 w-20" />
                  <Skeleton className="mx-auto mt-2 h-2.5 w-14" />
                  <Skeleton className="mx-auto mt-2 h-4 w-12 rounded-full" />
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <Skeleton className="h-5 w-48" />
            <ul className="mt-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Skeleton circle className="mt-1 h-2 w-2" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    );
  }

  const openEvaluate = () => {
    if (!data.activePeriod?.id) return;
    setEvaluateOpen(true);
  };

  const period = data.activePeriod;
  const stats = data.stats;

  // Aktif dönem yok — crash yerine anlamlı empty state.
  if (!period) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Dashboard" />
        <Card>
          <EmptyState
            icon="calendar"
            title="Aktif dönem yok"
            message="Dashboard görüntülemek için önce bir dönemi aktifleştirin. Dönemler sayfasından DRAFT bir dönemi seçip 'Aktifleştir' diyebilirsiniz."
            action={
              <Button
                variant="secondary"
                icon="refresh"
                onClick={() => {
                  dispatch(fetchPeriods());
                  dispatch(fetchDashboard());
                  // Dry-run state component local — Redux refresh kapsamı
                  // dışında. Aktif period varsa loadDryRun da tetiklenmeli,
                  // aksi halde "Son Dry-Run Simülasyonu" kartı stale kalıyordu.
                  if (activePeriodId) loadDryRun(activePeriodId, dryRunTab);
                }}
              >
                Yenile
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  // period.days_left backend hesaplamıyorsa viewModel None bırakır; subtitle'da
  // "—" fallback'i kullan.
  const daysLeftLabel = period.days_left != null ? `${period.days_left} gün` : '—';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        subtitle={`${period.name} — bitişine ${daysLeftLabel} kaldı`}
        actions={
          <>
            <Button
              variant="secondary"
              icon="refresh"
              onClick={() => {
                dispatch(fetchDashboard(selectedPeriodId || undefined));
                // Dry-run state component local — Redux refresh kapsamı dışında.
                // Aktif period varsa loadDryRun da tetiklenmeli, aksi halde
                // "Son Dry-Run Simülasyonu" kartı eski snapshot'ı göstermeye
                // devam ediyordu (kullanıcı F5 yapmak zorunda kalıyordu).
                if (activePeriodId) loadDryRun(activePeriodId, dryRunTab);
              }}
            >
              Yenile
            </Button>
            {/* Pending job varken butonu disable et — aynı dönem için ikinci
                bir job kuyruğa atılmasın. Worker per-period serial çalışıyor
                ama UI seviyesinde kullanıcının çift tıklamasını engellemek
                hem isteğin başarısız olmasını hem de iki ayrı pending state
                karışıklığını önlüyor. */}
            <Button
              icon={pendingJob ? 'clock' : 'play'}
              onClick={openEvaluate}
              disabled={!!pendingJob}
              title={pendingJob ? 'Mevcut hesaplama tamamlanana kadar bekleyin.' : ''}
            >
              {pendingJob ? 'Hesaplanıyor…' : 'Şimdi Hesapla'}
            </Button>
          </>
        }
      />

      {/* Aktif Dönem rozeti — birden fazla aktif dönem varsa rozetin yanına
          dropdown yerleştirilir. Tek aktif dönemde sadece statik rozet
          gösterilir (eski davranış korunur). */}
      {period && (
        <div className="flex items-center gap-3 self-start flex-wrap">
          <div className="flex items-center gap-2 rounded-full bg-success-50 ring-1 ring-success-200 px-3 py-1.5">
            <span className="h-2 w-2 rounded-full bg-success-500 animate-pulse" />
            <span className="text-xs font-medium text-success-800">
              Aktif Dönem: {period.name}
            </span>
            <span className="text-[10px] text-success-700">
              {formatDate(period.start_date)} — {formatDate(period.end_date)}
            </span>
          </div>
          {Array.isArray(data.activePeriods) && data.activePeriods.length > 1 && (
            <div className="flex items-center gap-2 rounded-full bg-violet-50 ring-1 ring-violet-200 px-3 py-1">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-500 text-[9px] font-bold text-white">
                {data.activePeriods.length}
              </span>
              <span className="text-[11px] font-medium text-violet-800 whitespace-nowrap">
                aktif dönem var — gösterilecek:
              </span>
              <select
                value={String(period.id)}
                onChange={(e) => setSelectedPeriodId(Number(e.target.value))}
                className="rounded-md border border-violet-300 bg-white px-2 py-0.5 text-xs font-medium text-violet-900 outline-none focus:ring-2 focus:ring-violet-300 cursor-pointer"
              >
                {data.activePeriods.map((p) => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon="tasks"
          accent="primary"
          label="Aktif Görev"
          value={stats.active_tasks}
          hint={`Dönem: ${period.name}`}
        />
        <StatCard
          icon="users"
          accent="success"
          label="Toplam Oyuncu"
          value={stats.total_players}
          delta={`${stats.total_sks} SKS`}
          deltaKind="neutral"
          hint={`${stats.total_managers} Yönetici`}
        />
        <StatCard
          icon="trophy"
          accent="warning"
          label="Baraj Puanı"
          value={data.barajPuan}
          hint={`/ ${data.totalMaxPoint} max`}
        />
        {pendingJob ? (
          // Polling devam ediyor — admin "Şimdi Hesapla"ya bastıktan sonra
          // worker job'ı bitirene kadar "Hesaplanıyor..." göstermek istiyoruz
          // ki kullanıcı eski "Başarılı/Başarısız" değerini gerçek sanmasın.
          <StatCard
            icon="clock"
            accent="primary"
            label="Son Hesaplama"
            value={
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                <span className="text-base font-medium text-primary-700">Hesaplanıyor…</span>
              </span>
            }
            delta={pendingJob.dryRun ? 'Dry-run' : 'Gerçek'}
            deltaKind="neutral"
            hint="Job kuyrukta — sonuç birkaç saniye içinde."
          />
        ) : (
          <StatCard
            icon="clock"
            accent={stats.last_evaluate_success ? 'success' : 'danger'}
            label="Son Hesaplama"
            value={stats.last_evaluate_time}
            delta={stats.last_evaluate_success ? 'Başarılı' : 'Başarısız'}
            deltaKind={stats.last_evaluate_success ? 'up' : 'down'}
            hint={CRON_LOCAL_LABEL}
          />
        )}
      </div>

      {/* ── Dry-Run Simülasyon Sonuçları ───────────────────────────────── */}
      <Card padded={false}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <Icon name="beaker" className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold text-neutral-800">Son Dry-Run Simülasyonu</span>
            {dryRunMeta && (
              <span className="text-xs text-neutral-400 ml-1">
                {dryRunMeta.snapshot_at
                  ? new Date(dryRunMeta.snapshot_at).toLocaleString('tr-TR', {
                      day: '2-digit', month: 'short',
                      hour: '2-digit', minute: '2-digit',
                    })
                  : 'Hesaplanıyor…'}
                {' · '}
                {dryRunMeta.user_count} kullanıcı
                {dryRunMeta.triggered_by && ` · ${dryRunMeta.triggered_by}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Tab seçici */}
            <div className="flex gap-1 text-xs">
              {['SKS', 'MANAGER'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setDryRunTab(t)}
                  className={`px-3 py-1 rounded-full font-medium transition-colors ${
                    dryRunTab === t
                      ? 'bg-violet-100 text-violet-700'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  {t === 'SKS' ? 'Genel Sıralama' : 'Yönetici HYO'}
                </button>
              ))}
            </div>
            {period?.id && (
              <button
                type="button"
                onClick={() => navigate(`/periods/${period.id}`)}
                className="text-xs text-violet-600 hover:text-violet-800"
              >
                Tüm sonuçlar →
              </button>
            )}
          </div>
        </div>

        {/* Uyarı bandı */}
        <div className="flex items-center gap-2 mx-5 mt-4 rounded-lg bg-violet-50 border border-violet-200 px-4 py-2.5">
          <Icon name="info" className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
          <p className="text-xs text-violet-700">
            Bu sonuçlar bir <strong>simülasyondur</strong> — hiçbir puan veritabanına yazılmamış, ASPara dağıtılmamıştır.
          </p>
        </div>

        <div className="px-5 pb-5 mt-4">
          {dryRunLoading && (
            <div className="py-8 text-center text-sm text-neutral-400">Yükleniyor…</div>
          )}

          {!dryRunLoading && !dryRunMeta && (
            <div className="py-8 text-center space-y-1">
              <Icon name="beaker" className="w-8 h-8 text-neutral-200 mx-auto" />
              <p className="text-sm text-neutral-400">Henüz dry-run çalıştırılmamış.</p>
              <p className="text-xs text-neutral-400">
                "Şimdi Hesapla" ile Dry-Run modunu seçin; sonuçlar burada görünür.
              </p>
            </div>
          )}

          {!dryRunLoading && dryRunMeta && dryRunRows.length > 0 && dryRunTab === 'SKS' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-200 text-neutral-500 text-left">
                    <th className="py-2 pr-3 font-medium w-10">Sıra</th>
                    <th className="py-2 pr-3 font-medium">Kullanıcı</th>
                    <th className="py-2 pr-3 font-medium">Müdürlük</th>
                    <th className="py-2 pr-3 font-medium text-right">Puan</th>
                    <th className="py-2 pr-3 font-medium text-right">ASPara</th>
                    <th className="py-2 font-medium text-center">Baraj</th>
                  </tr>
                </thead>
                <tbody>
                  {dryRunRows.map((r) => (
                    <tr
                      key={r.user_id}
                      className={`border-b border-neutral-100 ${r.below_baraj ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 pr-3 font-semibold text-neutral-700">{r.rank}</td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-neutral-800">{r.user_name_masked || r.user_id}</div>
                        <div className="text-neutral-400">{r.user_id}</div>
                      </td>
                      <td className="py-2 pr-3 text-neutral-500">
                        <div>{r.mudurluk || r.bolge || '—'}</div>
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold text-primary-700">
                        {Number(r.total_points).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                      </td>
                      <td className="py-2 pr-3 text-right text-neutral-600">
                        {formatASPara(r.estimated_aspara)}
                      </td>
                      <td className="py-2 text-center">
                        {r.below_baraj
                          ? <Badge color="red" size="sm">Altında</Badge>
                          : <Badge color="green" size="sm">Geçti</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!dryRunLoading && dryRunMeta && dryRunRows.length > 0 && dryRunTab === 'MANAGER' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-200 text-neutral-500 text-left">
                    <th className="py-2 pr-3 font-medium w-10">Sıra</th>
                    <th className="py-2 pr-3 font-medium">Yönetici</th>
                    <th className="py-2 pr-3 font-medium">Bölge Müdürlüğü</th>
                    <th className="py-2 pr-3 font-medium text-right">SKS</th>
                    <th className="py-2 pr-3 font-medium text-right">Gerçekleşen</th>
                    <th className="py-2 pr-3 font-medium text-right">HYO %</th>
                    <th className="py-2 font-medium text-center">Baraj</th>
                  </tr>
                </thead>
                <tbody>
                  {dryRunRows.map((r) => (
                    <tr
                      key={r.user_id}
                      className={`border-b border-neutral-100 ${r.below_baraj ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 pr-3 font-semibold text-neutral-700">{r.rank}</td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-neutral-800">{r.user_name_masked || r.user_id}</div>
                        <div className="text-neutral-400">{r.user_id}</div>
                      </td>
                      <td className="py-2 pr-3 text-neutral-500">{r.mudurluk || '—'}</td>
                      <td className="py-2 pr-3 text-right text-neutral-600">{r.sks_count}</td>
                      <td className="py-2 pr-3 text-right font-medium text-primary-700">
                        {r.gerceklesen_puan?.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold">
                        <span className={r.hyo_yuzde >= 100 ? 'text-green-600' : r.hyo_yuzde >= 70 ? 'text-amber-600' : 'text-red-500'}>
                          %{r.hyo_yuzde?.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        {r.below_baraj
                          ? <Badge color="red" size="sm">Altında</Badge>
                          : <Badge color="green" size="sm">Geçti</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!dryRunLoading && dryRunMeta && dryRunRows.length === 0 && (
            <p className="py-6 text-center text-sm text-neutral-400">Sonuç bulunamadı.</p>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Period progress */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Aktif Dönem İlerlemesi"
            subtitle={`${formatDate(period.start_date)} — ${formatDate(period.end_date)}`}
          />
          <div className="space-y-4">
            {data.periodProgress.length === 0 ? (
              <p className="text-xs text-neutral-500 py-4 text-center">
                Görev-seviyesi ilerleme henüz hesaplanmıyor.
              </p>
            ) : (
              data.periodProgress.map((p) => {
                // 2026-05-14: tier_breakdown geldiyse kademe bazlı ilerleme
                // göster (her eşikte kaç oyuncu o seviyeyi geçti). Tier'sız
                // görevler (örn. MANUAL) için eski avg_completion ProgressBar
                // korunur (fallback).
                const tiers = Array.isArray(p.tier_breakdown) ? p.tier_breakdown : [];
                return (
                  <div key={p.task_id}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-medium text-neutral-700">{p.task_name}</span>
                      <span className="text-xs text-neutral-500">
                        Max {p.max_points}p
                        {p.avg_completion != null && (
                          <span className="ml-2 text-neutral-400">
                            · Ort. %{p.avg_completion}
                          </span>
                        )}
                      </span>
                    </div>
                    {tiers.length > 0 ? (
                      <TierBreakdownBar tiers={tiers} />
                    ) : (
                      <ProgressBar
                        value={p.avg_completion}
                        color={PROGRESS_COLORS[p.color] || 'primary'}
                        showPercent={true}
                        size="md"
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Rewards */}
        <Card>
          <CardHeader title="Ödüller (ASPara)" subtitle="Kazanan sıralama — dönem sonunda verilir" />
          <div className="space-y-2">
            {data.rewards.map((r) => (
              <div
                key={r.rank}
                className="flex items-center justify-between rounded-lg border border-neutral-100 bg-neutral-50/50 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={
                      r.rank === 1
                        ? 'rank-gold'
                        : r.rank === 2
                          ? 'rank-silver'
                          : r.rank === 3
                            ? 'rank-bronze'
                            : 'font-semibold text-neutral-400'
                    }
                  >
                    #{r.rank}
                  </span>
                  <span className="text-xs text-neutral-500">Sıra</span>
                </div>
                <span className="text-sm font-semibold text-neutral-900">
                  {formatASPara(r.amount)} ASPara
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Icon name="info" className="inline h-3.5 w-3.5 mr-1" />
            Baraj ({data.barajPuan}p) altında kalan oyuncular ödülü dondurulmuş kabul edilir.
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Data transfers */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Veri Transfer Durumları"
            subtitle="Kaynak veri tablolarının son senkron tarihleri"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {data.dataTransfers.map((t) => {
              const statusMeta   = TRANSFER_STATUS[t.status]  || TRANSFER_STATUS.missing;
              const scheduleMeta = SCHEDULE_META[t.schedule]  || SCHEDULE_META.daily;
              const rowLabel     = formatRowCount(t.rowCount);
              const dateLabel    = formatTransferDate(t.date);
              return (
                <div
                  key={t.key || t.name}
                  className="rounded-lg border border-neutral-200 bg-white p-3 flex flex-col items-center gap-1"
                >
                  {/* Tablo adı + schedule rozeti */}
                  <div className="flex items-center justify-between w-full gap-1">
                    <p className="text-xs font-semibold text-neutral-900 truncate flex-1" title={t.key}>
                      {t.name}
                    </p>
                    <Badge color={scheduleMeta.color} size="sm">
                      {scheduleMeta.label}
                    </Badge>
                  </div>

                  {/* Son aktarım zamanı */}
                  <p className="text-[10px] text-neutral-500 w-full text-left">
                    {dateLabel ? `Son: ${dateLabel}` : 'Aktarım yok'}
                  </p>

                  {/* Satır sayısı */}
                  <p className="text-[10px] font-medium text-neutral-700 w-full text-left">
                    {rowLabel !== null ? rowLabel : '—'}
                  </p>

                  {/* Freshness durumu */}
                  <div className="w-full text-left mt-0.5">
                    <Badge color={statusMeta.color} size="sm">
                      {statusMeta.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Evaluate logs */}
        <Card>
          <CardHeader title="Son Hesaplama Logları" />
          <ul className="space-y-2">
            {data.evaluateLogs.map((log, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span
                  className={
                    'mt-1 h-2 w-2 rounded-full flex-shrink-0 ' +
                    (log.status === 'success'
                      ? 'bg-success-500'
                      : log.status === 'error'
                        ? 'bg-danger-500'
                        : log.status === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-primary-500')
                  }
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-neutral-900">{log.time}</span>
                    <Badge color={log.type === 'cron' ? 'blue' : 'purple'} size="sm">
                      {log.type === 'cron' ? 'Otomatik' : 'Manuel'}
                    </Badge>
                  </div>
                  <p className="text-xs text-neutral-600 mt-0.5">{log.message}</p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <EvaluateDialog
        period={evaluateOpen ? data.activePeriod : null}
        // Birden fazla aktif dönem varsa diyalog içinde de seçici çıkar —
        // admin "Şimdi Hesapla"ya bastıktan sonra hangi dönem için
        // çalıştırılacağını modal'da değiştirebilir. Tek dönemde dropdown
        // gizli kalır (eski davranış).
        availablePeriods={data.activePeriods || []}
        // Job kuyruğa alındığında pendingJob set edilir; polling effect
        // 1.5s sonra başlar, terminal status (SUCCESS/FAILED) görene kadar
        // devam eder. Bu sayede dashboard "Hesaplanıyor..." durumu gösterip
        // sonuç gelince otomatik güncelleniyor — admin manuel refresh
        // yapmak zorunda kalmıyor.
        onJobQueued={({ jobId, periodId, dryRun }) => {
          setPendingJob({ jobId, periodId, dryRun, startedAt: Date.now() });
        }}
        onClose={() => {
          setEvaluateOpen(false);
          dispatch(fetchDashboard(selectedPeriodId || undefined));
        }}
      />
    </div>
  );
}
